import type { Grower } from '@/lib/api/growers-api'
import type { GrowerFormValues } from '@/lib/schemas/grower-schemas'

/** Sentinel for the "no linked login account" option of the user select. */
export const NO_USER = 'none'

let areaDraftSeq = 0

/**
 * Khoá nháp cho một vùng trồng vừa được thêm trên form, chưa có id từ server.
 * Đếm tăng dần thay vì `crypto.randomUUID()`: hàm đó chỉ tồn tại trong secure
 * context, mở app qua HTTP trong mạng LAN là ném TypeError ngay lúc bấm "Thêm
 * vùng". Khoá chỉ cần duy nhất trong một phiên mở form.
 */
export function nextAreaDraftKey(): string {
    areaDraftSeq += 1
    return `new-area-${areaDraftSeq}`
}

/** Trims an API timestamp down to what input[type=date] accepts. */
export function toDateInput(v?: string) {
    return v ? v.slice(0, 10) : ''
}

/**
 * Maps a household record onto the shared grower form shape. Both the
 * agriculture create/edit page and the household's own read-only screen go
 * through here, so a field added to the form is picked up by both — keeping two
 * hand-written mappers in sync is exactly the kind of drift that stays silent
 * until a field mysteriously stops showing on one screen.
 *
 * `undefined` is the create case: every field starts empty.
 */
export function growerToFormValues(grower?: Grower): GrowerFormValues {
    return {
        name: grower?.name ?? '',
        nickname: grower?.nickname ?? '',
        birthday: toDateInput(grower?.birthday),
        phone: grower?.phone ?? '',
        address: grower?.address ?? '',
        national_id: grower?.national_id ?? '',
        national_id_issued_date: toDateInput(grower?.national_id_issued_date),
        national_id_issued_place: grower?.national_id_issued_place ?? '',
        bank_number: grower?.bank_number ?? '',
        bank_name: grower?.bank_name ?? '',
        bank_account: grower?.bank_account ?? '',
        areas:
            grower?.areas?.map((a, i) => ({
                // Round-trip the id so foreign references (care-log parcels)
                // survive an edit. Missing id → server mints one on save.
                id: a.id,
                // Suy ra từ dữ liệu, KHÔNG sinh ngẫu nhiên: hàm này chạy lại mỗi
                // lần render (prop `values` của useForm), khoá đổi theo là nháp
                // giấy tờ đất của vùng mất trắng giữa chừng.
                draftKey: a.id ?? (a.code ? `code-${a.code}` : `idx-${i}`),
                code: a.code ?? '',
                name: a.name ?? '',
                acreage: String(a.acreage ?? 0),
                note: a.note ?? '',
                // `?? []`: API bỏ key/`null` thì react-hook-form đọc undefined và giữ
                // nguyên giá trị của tab trước → chuyển tab rồi vẽ là toạ độ vùng này
                // ghi đè vùng kia. Mảng rỗng tường minh chặn đúng chỗ.
                paths: a.paths ?? [],
                markers: a.markers ?? [],
            })) ?? [],
        user_id: grower?.user_id ?? NO_USER,
    }
}
